import { useState } from "react";
import { Search } from "lucide-react";
import Button from "../../../../components/EcommerceActionButton/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/Dialog";
import { Badge } from "../../../../components/Badge";
import { catalogAccessories } from "../../data/stoneflexCatalog";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import { warningToast } from "../../../../services/toasts";
import styles from "./AccessorySelector.module.scss";

interface Accessory {
  id: string;
  reference: string;
  category: string;
  price: number;
  stock: number;
}

interface AccessorySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAccessory: (accessory: Accessory, quantity: number) => void;
  selectedAccessories: string[]; // IDs of already selected accessories
}

export function AccessorySelector({
  isOpen,
  onClose,
  onSelectAccessory,
  selectedAccessories,
}: AccessorySelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const accessories: Accessory[] = catalogAccessories;

  // Get unique categories
  const categories = [
    "all",
    ...Array.from(new Set(accessories.map((a) => a.category))),
  ];

  // Filter accessories based on search and category
  const filteredAccessories = accessories.filter((accessory) => {
    const matchesSearch = accessory.reference
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || accessory.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  /**
   * Handle adding an accessory to the quotation
   */
  const handleAddAccessory = (accessory: Accessory) => {
    const quantity = quantities[accessory.id] || 1;
    onSelectAccessory(accessory, quantity);

    if (quantity > accessory.stock) {
      warningToast(
        `Cantidad solicitada (${quantity} unidades) supera el stock disponible (${accessory.stock}).`
      );
    }

    setQuantities((prev) => ({ ...prev, [accessory.id]: 1 }));
  };

  /**
   * Handle quantity change for an accessory
   */
  const handleQuantityChange = (accessoryId: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [accessoryId]: Math.max(1, value) }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader className={styles.dialogHeader}>
          <DialogTitle className={styles.dialogTitle}>
            Seleccionar Accesorios
          </DialogTitle>
          <DialogDescription className={styles.dialogDescription}>
            Elige los accesorios que deseas agregar a tu pedido.
          </DialogDescription>
        </DialogHeader>

        {/* Search and Filter */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersContainer}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar accesorios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={styles.selectFilter}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === "all" ? "Todas las categorías" : cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Accessories List */}
        <div className={styles.accessoriesList}>
          <div className={styles.accessoriesContainer}>
            {filteredAccessories.map((accessory) => {
              const isSelected = selectedAccessories.includes(accessory.id);
              const isOutOfStock = accessory.stock === 0;

              return (
                <div
                  key={accessory.id}
                  className={`${styles.accessoryCard} ${
                    isSelected ? styles.selected : ""
                  }`}
                >
                  <div className={styles.accessoryInfo}>
                    <div className={styles.accessoryHeader}>
                      <div className={styles.accessoryDetails}>
                        <h4 className={styles.accessoryName}>
                          {accessory.reference}
                        </h4>
                        <div className={styles.accessoryMetadata}>
                          <Badge variant="outline" className="text-xs">
                            {accessory.category}
                          </Badge>
                          <span
                            className={`${styles.stockBadge} ${
                              accessory.stock === 0
                                ? styles.outOfStock
                                : accessory.stock < 10
                                ? styles.lowStock
                                : styles.inStock
                            }`}
                          >
                            Stock: {accessory.stock}
                          </span>
                        </div>
                      </div>
                      <div className={styles.accessoryPrice}>
                        <div className={styles.price}>
                          {formatNumberToPrice(accessory.price)}
                        </div>
                        <div className={styles.priceNote}>+ IVA</div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.accessoryActions}>
                    {!isOutOfStock && !isSelected && (
                      <>
                        <input
                          type="number"
                          min="1"
                          value={quantities[accessory.id] || 1}
                          onChange={(e) =>
                            handleQuantityChange(
                              accessory.id,
                              parseInt(e.target.value) || 1
                            )
                          }
                          className={styles.quantityInput}
                        />
                        <Button
                          color="primary"
                          shape="little-rounded"
                          onClick={() => handleAddAccessory(accessory)}
                        >
                          Agregar
                        </Button>
                      </>
                    )}
                    {isSelected && <Badge variant="success">Agregado</Badge>}
                    {isOutOfStock && <Badge variant="danger">Agotado</Badge>}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredAccessories.length === 0 && (
            <div className={styles.emptyState}>
              <p>No se encontraron accesorios</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button color="outlined" shape="little-rounded" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
